import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Switch,
} from "@mui/material";

import { Row, Label, SwitchRow } from "../index.styles";
import { DecimalInput, Select } from "../../../components";
import DraggableInputs from "../../../components/Draggableinput";

import { DURATION_OPTIONS } from "../../ProductAnalyzer/common/DurationFilter";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectProductAnalyzerSettings } from "../../../features/settings/settings.selector";
import { updateProductAnalyzerSettings } from "../../../features/settings/settings.slice";
import {
  IProductAnalyzerSettingsState,
  ITag,
  TDefaultChartTimeSpan,
} from "../../../features/settings/settings.types";
import TagMenu from "./TagMenu";
import { updateProductAnalyzerSettingsThunk } from "../../../features/settings/settings.thunk";
import { toNumber } from "../../../helpers";
import {
  MAX_BSR_VALIDATION_ERROR,
  MIN_BSR_VALIDATION_ERROR,
} from "../../../constants";

type TErrors = {
  minBsr: string | null;
  maxBsr: string | null;
};

const ProductAnalyzerSettings = () => {
  const dispatch = useAppDispatch();
  const productAnalyzerSettings = useAppSelector(selectProductAnalyzerSettings);
  const [errors, setErrors] = useState<TErrors>({ minBsr: null, maxBsr: null });
  const [minBsr, setMinBsr] = useState<number>(productAnalyzerSettings.minBsr);
  const [maxBsr, setMaxBsr] = useState<number>(productAnalyzerSettings.maxBsr);
  const [tagMenuEl, setTagMenuEl] = useState<null | HTMLElement>(null);
  const [tagToEdit, setTagToEdit] = useState<null | ITag>(null);
  const isTagMenuOpened = Boolean(tagMenuEl);

  useEffect(() => {
    if (minBsr > maxBsr) {
      setErrors({
        minBsr: MIN_BSR_VALIDATION_ERROR,
        maxBsr: MAX_BSR_VALIDATION_ERROR,
      });
    } else {
      setErrors({
        minBsr: null,
        maxBsr: null,
      });
    }
  }, [minBsr, maxBsr]);

  if (!productAnalyzerSettings) return null;

  return (
    <Grid
      container
      spacing="16px"
      sx={{
        overflowX: "visible",
        flexWrap: "wrap",
        "@media (max-width:319px)": {
          overflowX: "auto",
          flexWrap: "nowrap",
          justifyContent: "flex-start",
        },
      }}
    >
      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          minWidth: "auto",
          "@media (max-width:319px)": {
            minWidth: "320px",
          },
        }}
      >
        <Stack spacing={1}>
          <Divider>Buying Criteria</Divider>

          <Row>
            <Label
              error={!!errors["minBsr"]}
              tooltip="SellerScout highlights if the product meets this minimum Best Sellers Rank requirement (as a percentage, within the category)."
            >
              Minimum BSR(%)
            </Label>
            <DecimalInput
              suffix="%"
              value={productAnalyzerSettings.minBsr}
              onChange={(val: number) => {
                if (val <= productAnalyzerSettings.maxBsr) {
                  dispatch(
                    updateProductAnalyzerSettingsThunk({
                      minBsr: val,
                      maxBsr,
                    }),
                  );
                }
              }}
              onEditing={setMinBsr}
              align="right"
              min={1}
              max={100}
              step={1}
              error={!!errors["minBsr"]}
              helperText={errors["minBsr"] || ""}
            />
          </Row>

          <Row>
            <Label
              error={!!errors["maxBsr"]}
              tooltip="SellerScout highlights if the product meets this maximum Best Sellers Rank requirement (as a percentage, within the category)."
            >
              Maximum BSR(%)
            </Label>
            <DecimalInput
              suffix="%"
              value={productAnalyzerSettings.maxBsr}
              onChange={(val: number) => {
                if (val >= productAnalyzerSettings.minBsr) {
                  dispatch(
                    updateProductAnalyzerSettingsThunk({
                      minBsr,
                      maxBsr: val,
                    }),
                  );
                }
              }}
              onEditing={setMaxBsr}
              align="right"
              min={1}
              max={100}
              step={1}
              error={!!errors["maxBsr"]}
              helperText={errors["maxBsr"] || ""}
            />
          </Row>

          <Row>
            <Label tooltip="SellerScout highlights if the product meets this minimum profit. Also used to calculate the max cost to meet this requirement.">
              Minimum Profit
            </Label>
            <DecimalInput
              prefix="$"
              value={productAnalyzerSettings.minProfit}
              onChange={(minProfit: number) => {
                dispatch(updateProductAnalyzerSettingsThunk({ minProfit }));
              }}
              align="right"
            />
          </Row>

          <Row>
            <Label tooltip="SellerScout highlights if the product meets this minimum return on investment. Also used to calculate the max cost to meet this requirement.">
              Minimum ROI
            </Label>
            <DecimalInput
              suffix="%"
              value={productAnalyzerSettings.minRoi}
              onChange={(minRoi: number) => {
                dispatch(updateProductAnalyzerSettingsThunk({ minRoi }));
              }}
              align="right"
            />
          </Row>

          <Divider sx={{ pt: 2 }}>Default Values</Divider>

          <Row>
            <Label tooltip="The default time frame for the ranks and prices panel. Select either current or an average over a specified period.">
              Ranks & Prices Time Frame
            </Label>
            <Select
              value={productAnalyzerSettings.defaultRanksAndPricesTimeFrame}
              onChange={(e) =>
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    defaultRanksAndPricesTimeFrame: e.target.value as string,
                  }),
                )
              }
              size="large"
              fullWidth
            >
              {DURATION_OPTIONS.map((option) => (
                <MenuItem
                  key={`ranks_and_prices_time_frame_${option.id}`}
                  value={option.id}
                >
                  {option.tooltip}
                </MenuItem>
              ))}
            </Select>
          </Row>

          <Row>
            <Label tooltip="The default time frame for the buy box analysis panel.">
              Buy Box Analysis Time Frame
            </Label>
            <Select
              value={productAnalyzerSettings.defaultBbAnalysisTimeFrame}
              onChange={(e) =>
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    defaultBbAnalysisTimeFrame: e.target.value as string,
                  }),
                )
              }
              size="large"
              fullWidth
            >
              {DURATION_OPTIONS.filter((option) => !!option.range).map(
                (option) => (
                  <MenuItem
                    key={`bb_analysis_time_frame_${option.id}`}
                    value={option.id}
                  >
                    {option.tooltip}
                  </MenuItem>
                ),
              )}
            </Select>
          </Row>

          <Row>
            <Label tooltip="Your cost to fulfill FBM orders to be included in the calculations. Can be manually adjusted as you analyze.">
              FBM Cost
            </Label>
            <DecimalInput
              prefix="$"
              value={productAnalyzerSettings.defaultFbmCost}
              onChange={(defaultFbmCost: number) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({ defaultFbmCost }),
                );
              }}
              align="right"
            />
          </Row>

          <Row>
            <Label tooltip="Default value for the customizable field within the ROI panel. This can be manually adjusted as you analyze.">
              Custom ROI Calc
            </Label>
            <DecimalInput
              suffix="%"
              value={productAnalyzerSettings.defaultCustomRoiCalc}
              onChange={(defaultCustomRoiCalc: number) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({ defaultCustomRoiCalc }),
                );
              }}
              align="right"
            />
          </Row>

          <Divider sx={{ pt: 2 }}>Quick Info</Divider>

          <SwitchRow>
            <Label tooltip="Show the calculated profit in the Quick Info panel.">
              Show Profit
            </Label>
            <Switch
              checked={productAnalyzerSettings.showProfitInQuickInfo}
              onChange={(_e, checked: boolean) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    showProfitInQuickInfo: checked,
                  }),
                );
              }}
            />
          </SwitchRow>

          <SwitchRow>
            <Label tooltip="Show the calculated profit margin in the Quick Info panel">
              Show Profit Margin
            </Label>
            <Switch
              checked={productAnalyzerSettings.showProfitMarginInQuickInfo}
              onChange={(_e, checked: boolean) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    showProfitMarginInQuickInfo: checked,
                  }),
                );
              }}
            />
          </SwitchRow>

          <SwitchRow>
            <Label tooltip="Show the calculated ROI in the Quick Info panel">
              Show ROI
            </Label>
            <Switch
              checked={productAnalyzerSettings.showRoiInQuickInfo}
              onChange={(_e, checked: boolean) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    showRoiInQuickInfo: checked,
                  }),
                );
              }}
            />
          </SwitchRow>

          <SwitchRow>
            <Label tooltip="Show the sale price needed to sell at to breakeven in the Quick Info panel">
              Show Breakeven
            </Label>
            <Switch
              checked={productAnalyzerSettings.showBreakevenInQuickInfo}
              onChange={(_e, checked: boolean) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    showBreakevenInQuickInfo: checked,
                  }),
                );
              }}
            />
          </SwitchRow>

          <SwitchRow>
            <Label tooltip="Show offer counts summary in the Quick Info panel">
              Show Offer Summary
            </Label>
            <Switch
              checked={productAnalyzerSettings.showOfferSummaryInQuickInfo}
              onChange={(_e, checked: boolean) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    showOfferSummaryInQuickInfo: checked,
                  }),
                );
              }}
            />
          </SwitchRow>

          <Row>
            <Label tooltip="The default time frame for average.">
              Average Time Frame
            </Label>
            <Select
              value={productAnalyzerSettings.defaultAverageTimeFrameInQuickInfo}
              onChange={(e) =>
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    defaultAverageTimeFrameInQuickInfo: e.target
                      .value as string,
                  }),
                )
              }
              size="large"
              fullWidth
            >
              {DURATION_OPTIONS.map((option) => (
                <MenuItem
                  key={`average_time_frame_${option.id}`}
                  value={option.id}
                >
                  {option.tooltip}
                </MenuItem>
              ))}
            </Select>
          </Row>

          <Row>
            <Label tooltip="The default time frame for buybox average.">
              Buybox Average Time Frame
            </Label>
            <Select
              value={
                productAnalyzerSettings.defaultBBAverageTimeFrameInQuickInfo
              }
              onChange={(e) =>
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    defaultBBAverageTimeFrameInQuickInfo: e.target
                      .value as string,
                  }),
                )
              }
              size="large"
              fullWidth
            >
              {DURATION_OPTIONS.map((option) => (
                <MenuItem
                  key={`bb_average_time_frame_${option.id}`}
                  value={option.id}
                >
                  {option.tooltip}
                </MenuItem>
              ))}
            </Select>
          </Row>

          <Divider sx={{ pt: 2 }}>Charts</Divider>

          <SwitchRow>
            <Label tooltip="Show the BSR & Prices chart in the Charts panel.">
              Show Prices Chart
            </Label>
            <Switch
              checked={productAnalyzerSettings.showPricesChart}
              onChange={(_e, checked: boolean) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    showPricesChart: checked,
                  }),
                );
              }}
            />
          </SwitchRow>

          <SwitchRow>
            <Label tooltip="Show the Monthly Sold chart in the Charts panel.">
              Show Sold Chart
            </Label>
            <Switch
              checked={productAnalyzerSettings.showSoldChart}
              onChange={(_e, checked: boolean) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    showSoldChart: checked,
                  }),
                );
              }}
            />
          </SwitchRow>

          <SwitchRow>
            <Label tooltip="Show the Offers, Reviews and Ratings chart in the Charts panel.">
              Show Offers Chart
            </Label>
            <Switch
              checked={productAnalyzerSettings.showOffersChart}
              onChange={(_e, checked: boolean) => {
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    showOffersChart: checked,
                  }),
                );
              }}
            />
          </SwitchRow>

          <Row>
            <Label tooltip="Limit the history shown on the charts. Shorter periods improve performance.">
              Chart Time Span
            </Label>

            <Select
              value={productAnalyzerSettings.defaultChartTimeSpan || "OneMonth"}
              onChange={(e) =>
                dispatch(
                  updateProductAnalyzerSettingsThunk({
                    defaultChartTimeSpan: e.target
                      .value as TDefaultChartTimeSpan,
                  }),
                )
              }
              size="large"
              fullWidth
            >
              <MenuItem value={"OneDay"}>One day</MenuItem>
              <MenuItem value={"OneWeek"}>One week</MenuItem>
              <MenuItem value={"OneMonth"}>One month</MenuItem>
              <MenuItem value={"ThreeMonths"}>Three months</MenuItem>
              <MenuItem value={"OneYear"}>One year</MenuItem>
            </Select>
          </Row>
        </Stack>
      </Grid>

      <Grid
        size={{ xs: 12, md: 6 }}
        sx={{
          minWidth: "auto",
          "@media (max-width:319px)": {
            minWidth: "320px",
          },
        }}
      >
        <Divider>Tags</Divider>

        <Stack direction="row" gap="6px" marginTop="8px">
          {productAnalyzerSettings.tags.map((tag) => (
            <Button
              id={`tag-button-${tag.id}`}
              variant="outlined"
              aria-controls={isTagMenuOpened ? "tag-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={isTagMenuOpened ? "true" : undefined}
              onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                setTagMenuEl(event.currentTarget);
                setTagToEdit(tag);
              }}
            >
              {tag.caption}
            </Button>
          ))}
          <Button
            variant="contained"
            id="tag-button"
            aria-controls={isTagMenuOpened ? "tag-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={isTagMenuOpened ? "true" : undefined}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              setTagMenuEl(event.currentTarget);
              setTagToEdit(null);
            }}
          >
            New Tag
          </Button>
        </Stack>

        <TagMenu
          open={isTagMenuOpened}
          anchorEl={tagMenuEl}
          tag={tagToEdit}
          onClose={() => setTagMenuEl(null)}
        />

        <Divider sx={{ pt: 2 }}>Panels</Divider>

        <SwitchRow>
          <Label tooltip="Show/hide jump to panel buttons in mobile device and chrome extension.">
            Show Jump Buttons
          </Label>
          <Switch
            checked={productAnalyzerSettings.showJumpButtons}
            onChange={(_e, checked: boolean) => {
              dispatch(
                updateProductAnalyzerSettingsThunk({
                  showJumpButtons: checked,
                }),
              );
            }}
          />
        </SwitchRow>

        <Box paddingX={1}>
          <Alert severity="info">
            Drag the panel headings to change the order of the panels. Toggling
            a panel off removes it from product analyzer.
          </Alert>
        </Box>

        <Stack className="sas-panels-form" padding={1} spacing={1}>
          <DraggableInputs data={productAnalyzerSettings.panelOrder} />
        </Stack>
      </Grid>
    </Grid>
  );
};

export default ProductAnalyzerSettings;
